import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/storybook/components/Toast/Toast';

const COPY_FEEDBACK_DURATION_MS = 1000;

interface UseCopyToClipboardOptions {
  showErrorToast?: boolean;
  resetAfterMs?: number | null;
}

interface UseCopyToClipboardResult {
  isCopied: boolean;
  copy: (text: string) => void;
  reset: VoidFunction;
}

export const useCopyToClipboard = ({
  showErrorToast = false,
  resetAfterMs = COPY_FEEDBACK_DURATION_MS,
}: UseCopyToClipboardOptions = {}): UseCopyToClipboardResult => {
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied || resetAfterMs === null) {
      return undefined;
    }

    const timer = window.setTimeout(() => setIsCopied(false), resetAfterMs);

    return () => window.clearTimeout(timer);
  }, [isCopied, resetAfterMs]);

  const handleCopyError = useCallback(() => {
    setIsCopied(false);
    if (showErrorToast) {
      makeToast({
        content: String(t('global.copyFailed')),
        appearance: 'error',
      });
    }
  }, [makeToast, t, showErrorToast]);

  const copy = useCallback(
    (text: string) => {
      if (!text) {
        return;
      }

      if (!navigator.clipboard) {
        handleCopyError();
        return;
      }

      setIsCopied(true);

      void navigator.clipboard.writeText(text).catch(handleCopyError);
    },
    [handleCopyError],
  );

  const reset = useCallback(() => {
    setIsCopied(false);
  }, []);

  return { isCopied, copy, reset };
};
